import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowRight, Check } from 'lucide-react'
import { PageHeader, Panel, Banner, EmptyState } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { money, num, dateShort } from '../../lib/format'
import { Icon } from '../../components/Icon'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { Term } from '../../components/Term'
import { TierBadge, LeadStatus, leadCost, monthName, type Lead } from './ClientLeads'
import { ClientAlerts } from './ClientAlerts'

export type Tier = { key: string; name: string; price_cents: number; description: string | null }
export type Summary = {
  client: { id: string; name: string; status: string; category: string | null; areas: string[]; tiers_wanted: string[] | null; weekly_lead_limit: number | null }
  org: { name: string; timezone: string; dispute_window_hours: number; pricing_mode: string; can_pay_online: boolean }
  wallet: {
    credit_cents: number; cash_cents: number; pending_cents: number; pending_leads: number; overage_this_month_cents: number
    auto_buy: boolean; auto_buy_cap_cents: number | null; auto_buy_room_cents: number | null; available_cents: number | null
    deposited_this_month_cents: number; monthly_minimum_cents: number; minimum_met: boolean; discount_bps: number
  }
  tiers: Tier[]
  month: string
  this_month: { leads: number; list_cents: number; paid_cents: number; overage_cents: number; contacted: number }
  recent: Lead[]
  history: { month: string; short: string; paid_cents: number; list_cents: number; leads: number }[]
  by_tier: { key: string; name: string; rank: number; leads: number; paid_cents: number }[]
  credit_series: { day: string; credit_cents: number }[]
  unseen_leads: number
  contact: { email: string | null; phone: string | null }
  setup: { card: boolean; monthly_payment: boolean; refill: boolean; profile: boolean; business?: boolean; password?: boolean; alerts?: boolean; facts?: boolean }
}

/** "20%" from 2000 basis points; keeps one decimal only when it needs one. */
export const pct = (bps: number) => `${Number((bps / 100).toFixed(1))}%`
/** What $100 put in up front becomes as lead credit. */
export const creditFor100 = (bps: number) => (bps > 0 && bps < 10000 ? Math.round(10000 / (1 - bps / 10000)) : 10000)

const greeting = () => {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
}

export default function ClientOverview() {
  const { data, error, reload } = useApi<Summary>('/api/client/summary')
  const [params, setParams] = useSearchParams()
  const [paid] = useState(() => params.get('paid'))
  useEffect(() => { if (params.get('paid')) setParams({}, { replace: true }) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />

  const { wallet: w, this_month: m, org } = data
  const saved = Math.max(0, m.list_cents - m.paid_cents)
  const minPct = w.monthly_minimum_cents ? Math.min(100, Math.round((w.deposited_this_month_cents / w.monthly_minimum_cents) * 100)) : 100
  const typical = data.tiers.find((t) => t.key === 'b') || data.tiers[Math.floor(data.tiers.length / 2)]
  const leadsLeft = typical ? Math.floor(Math.max(0, w.credit_cents - w.pending_cents) / typical.price_cents) : null

  return (
    <>
      <PageHeader title={`${greeting()}, ${data.client.name}`} subtitle={`Your leads from ${org.name}, what they cost, and what is left to spend.`} />
      {paid === '1' && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="pos" title="Payment received">Paid by card, your lead credit goes up within a minute. Paid by bank, it goes up when the payment clears, usually 3 to 5 business days; Billing shows it as processing until then.</Banner></div>}
      {paid === '0' && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title="No payment made">The payment page was closed. Nothing was charged.</Banner></div>}

      <SetupChecklist setup={data.setup} canPay={org.can_pay_online} orgName={org.name} onChanged={reload} />
      {data.unseen_leads > 0 && <NewLeads leads={data.recent.filter((l) => !l.client_seen_at)} count={data.unseen_leads} />}

      <div className="portal-hero">
        <section className="hero-card is-dark" aria-label="Lead credit">
          <span className="hero-kicker"><Term tip="credit">Lead credit</Term></span>
          <span className="hero-figure">{money(w.credit_cents)}</span>
          <span className="hero-sub muted">
            {leadsLeft != null && typical ? `About ${leadsLeft} ${typical.name} lead${leadsLeft === 1 ? '' : 's'} left` : 'Your balance at normal lead prices'}
            {w.pending_leads > 0 ? `, after ${money(w.pending_cents)} on the way in ${w.pending_leads} lead${w.pending_leads === 1 ? '' : 's'}` : ''}
          </span>
          <div className="stack-tight" style={{ gap: 'var(--s-2)' }}>
            <div className="meter-row"><span>{monthName(data.month)}: {money(w.deposited_this_month_cents)} of the {money(w.monthly_minimum_cents)} minimum</span><span>{minPct}%</span></div>
            <div className={minPct >= 100 ? 'meter is-full' : 'meter'}><span style={{ width: `${minPct}%` }} /></div>
          </div>
          <div className="hero-actions">
            <Link className="btn btn-primary" to="/portal/billing">{org.can_pay_online ? 'Add money' : 'Billing'} <Icon of={ArrowRight} size={15} /></Link>
            <span className="hero-note">{money(10000)} buys {money(creditFor100(w.discount_bps))} of leads</span>
          </div>
        </section>

        <Panel>
          <div className="panel-header"><h2 className="panel-title">{monthName(data.month)}</h2><Link to="/portal/spending">Spending</Link></div>
          <dl className="month-stats">
            <div><dt>Leads</dt><dd>{num(m.leads)}<span>{m.leads ? `${num(m.contacted)} reached out` : 'none yet'}</span></dd></div>
            <div><dt>Spent</dt><dd>{money(m.paid_cents)}<span>{saved > 0 ? `${money(saved)} saved by prepaying` : 'at your prepaid prices'}</span></dd></div>
            <div><dt><Term tip="bill">On your bill</Term></dt><dd>{money(w.overage_this_month_cents)}<span>{!w.auto_buy ? 'leads pause when credit runs out' : 'billed on the 1st'}</span></dd></div>
          </dl>
        </Panel>
      </div>

      <Panel>
        <div className="panel-header"><h2 className="panel-title">Latest leads</h2><Link to="/portal/leads">All leads</Link></div>
        {data.recent.length === 0 ? <EmptyState title="No leads yet" body="Each lead shows up here the moment a rep recommends you." /> : (
          <div className="lead-list">
            {data.recent.slice(0, 5).map((l) => (
              <Link key={l.id} to={`/portal/leads/${l.id}`} className="lead-list-row">
                <span className="lead-list-badge">{l.tier_name ? <TierBadge name={l.tier_name} tierKey={l.tier_key} /> : null}</span>
                <span className="lead-list-body">
                  <strong>{l.summary || 'Asked for a recommendation'}</strong>
                  <span className="muted">{[l.area_name || l.area_text, dateShort(l.sent_at)].filter(Boolean).join(' · ')}</span>
                </span>
                <LeadStatus lead={l} />
                <span className="lead-list-cost">{l.grading ? <span className="muted">Not charged yet</span> : money(leadCost(l))}</span>
              </Link>
            ))}
          </div>
        )}
      </Panel>

      <div className="help-strip">
        <div>
          <strong>Questions?</strong>
          <span className="muted">Talk to {org.name} about a lead, a bill, or what you would like more of.</span>
        </div>
        <div className="help-strip-links">
          {data.contact.email && <a href={`mailto:${data.contact.email}`}>{data.contact.email}</a>}
          {data.contact.phone && <a href={`tel:${data.contact.phone.replace(/[^0-9+]/g, '')}`}>{data.contact.phone}</a>}
          <Link to="/portal/resources">How it works</Link>
        </div>
      </div>
    </>
  )
}

/**
 * Get set up: how leads reach a business, in one line, then every step with the
 * next one open. Lead alerts come first (a buzz the moment a lead comes in; it
 * can wait on this device), then what the two-minute sign-up left for later.
 * Gone once everything is done.
 */
function SetupChecklist({ setup, canPay, orgName, onChanged }: { setup: Summary['setup']; canPay: boolean; orgName: string; onChanged: () => void }) {
  const [later, setLater] = useState(() => { try { return localStorage.getItem('alerts-later') === '1' } catch { return false } })
  const all = [
    { key: 'alerts', done: setup.alerts !== false, label: 'Turn on lead alerts on your phone', why: '', to: '/portal/setup' },
    ...(canPay ? [
      { key: 'card', done: setup.card, label: 'Set up payments', why: 'Save a card and choose your monthly payment, in three short steps.', to: '/portal/billing/setup' },
      { key: 'monthly', done: setup.monthly_payment, label: 'Turn on your monthly payment', why: 'Your monthly amount, added as credit on the 1st, at the discount.', to: '/portal/billing/setup' },
    ] : []),
    { key: 'password', done: setup.password !== false, label: 'Set a password', why: 'So you can sign in on any device. It takes a moment.', to: '/portal/setup' },
    { key: 'business', done: setup.business !== false, label: 'Finish your business details', why: 'Your legal name, address and website. The Business Terms are signed in your legal name.', to: '/portal/setup' },
    { key: 'profile', done: setup.profile, label: 'Fill in your business profile', why: 'Where you work, what you do, and the leads you want. Leads are matched to you on it.', to: '/portal/profile' },
    { key: 'facts', done: setup.facts !== false, label: 'Add a few personal facts', why: 'Under For your replies. Responders use one each time they recommend you, like "the owner still walks every roof himself". Only what is true.', to: '/portal/profile' },
  ]
  // A card saved with the first tab already set up payments: those steps only show while they are still to do.
  const steps = all.filter((s) => !(s.done && (s.key === 'card' || s.key === 'monthly')))
  const left = steps.filter((s) => !s.done)
  // Alerts put off on this device wait in the list (linked to Finish setting up); the rest decide whether it shows.
  const pending = left.filter((s) => !(s.key === 'alerts' && later))
  if (pending.length === 0) return null
  const open = pending[0]
  const putOff = () => { setLater(true); try { localStorage.setItem('alerts-later', '1') } catch { /* fine */ } }
  return (
    <section className="getset" aria-label="Get set up">
      <div className="getset-head">
        <h2 className="panel-title">Get set up</h2>
        <span className="muted">{steps.length - left.length} of {steps.length} done</span>
      </div>
      <p className="getset-how">How it works: when someone in a local Facebook group asks for what you do, a {orgName} rep recommends you. Each lead shows up here under Leads.</p>
      <ol className="getset-steps">
        {steps.map((s, i) => (
          <li key={s.key} className={s.done ? 'is-done' : s === open ? 'is-open' : ''}>
            <span className="getset-mark" aria-hidden="true">{s.done ? <Icon of={Check} size={14} /> : i + 1}</span>
            <div className="getset-body">
              {s.done ? <span>{s.label}<span className="sr-only"> (done)</span></span> : s === open ? <strong>{s.label}</strong> : <Link to={s.to}>{s.label}</Link>}
              {s === open && s.key === 'alerts' && (
                <div className="getset-action">
                  <ClientAlerts hasPassword={setup.password !== false} onChange={onChanged} />
                  <button type="button" className="link-button" onClick={putOff}>Do this later</button>
                </div>
              )}
              {s === open && s.key !== 'alerts' && (
                <div className="getset-action">
                  <span className="muted">{s.why}</span>
                  <Link className="btn btn-primary" to={s.to}>Start <Icon of={ArrowRight} size={15} /></Link>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

/** The leads that came in since the client last looked, first thing on the page, each one tap to answer. */
function NewLeads({ leads, count }: { leads: Lead[]; count: number }) {
  return (
    <div style={{ marginBottom: 'var(--s-6)' }}>
      <Panel>
        <div className="panel-header">
          <h2 className="panel-title">{count} new lead{count === 1 ? '' : 's'} for you</h2>
          {count > leads.length && <Link to="/portal/leads">See all {count}</Link>}
        </div>
        <div className="new-list">
          {leads.slice(0, 3).map((l) => (
            <Link key={l.id} to={`/portal/leads/${l.id}`} className="new-row">
              <span className="new-row-badge">{l.tier_name ? <TierBadge name={l.tier_name} tierKey={l.tier_key} /> : null}</span>
              <span style={{ minWidth: 0 }}>
                <span className="new-row-title" style={{ display: 'block' }}>{l.summary || 'Asked for a recommendation'}</span>
                <span className="muted" style={{ fontSize: 13 }}>{[l.area_name || l.area_text, l.group_name].filter(Boolean).join(' · ')}</span>
              </span>
              <span className="btn btn-secondary new-row-open">Open</span>
            </Link>
          ))}
        </div>
      </Panel>
    </div>
  )
}
